import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { TreatmentPlanDetailResponse } from '../../../../models/api/view/claim/details/case-management/treatment-plan-detail-response';
import { BaseComponent } from '../../../base/base.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { BackButtonComponent } from '../../../core/buttons/back-button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { FormatPipe } from '../../../../pipes/format.pipe';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-case-treatment-plan',
  imports: [
    CardComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TableComponent,
    ColumnComponent,
    EmptyStateComponent,
    ProgressbarComponent,
    BackButtonComponent,
    PageHeaderComponent,
    ActionRowComponent,
    FormatPipe,
  ],
  template: `
    <div class="tw-pb-2">
      <app-action-row>
        <app-back-button left1 (click)="onBackClick()"
          >Back to Treatment Plans List</app-back-button
        >
      </app-action-row>
    </div>
    <app-page-header label="Treatment Plan Detail" />

    @if (loading) {
      <app-empty-state
        height="300px"
        icon="fa-light fa-clipboard-list-check"
        label="Loading Treatment Plan"
        message="Retrieving treatment plan details"
      >
        <div class="tw-py-4">
          <app-progressbar />
        </div>
      </app-empty-state>
    } @else if (error) {
      <app-empty-state
        icon="fa-light fa-triangle-exclamation"
        label="An error has occurred"
      />
    } @else if (!dataSource) {
      <app-empty-state
        icon="fa-light fa-clipboard-list-check"
        label="No data available"
        message="Treatment plan details could not be loaded"
      />
    } @else {
      <div
        class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 md:tw-gap-8 tw-pb-4 tw-pt-6"
      >
        <div class="tw-w-full xl:tw-basis-1/3">
          @if (isNonUR) {
            <app-card label="Non-UR Treatment">
              <app-detail-panel [dataSource]="dataSource.detail">
                <app-detail-item label="Approved By" name="approvedBy" />
                <app-detail-item
                  label="Approved By Other"
                  name="approvedByOther"
                />
              </app-detail-panel>
            </app-card>
          } @else {
            <app-card label="Detail">
              <app-detail-panel [dataSource]="dataSource.detail">
                <app-detail-item
                  label="Received Date Time"
                  name="receivedDate"
                  format="dt"
                />
                <app-detail-item label="Type" name="type" />
                <app-detail-item label="Subtype" name="subType" />
                <app-detail-item label="DCN" name="dcn" labelCase="none" />
                <app-detail-item
                  label="Texas voluntary"
                  name="texasVoluntary"
                  format="b"
                  labelCase="none"
                />
              </app-detail-panel>
            </app-card>
          }
        </div>
        <div class="tw-w-full xl:tw-basis-1/3">
          <app-card
            label="Facility"
            emptyIcon="fa-light fa-building"
            emptyLabel="No Facility Information"
            emptyMessage="There is no information about Facility"
            [showEmptyState]="!facilityHasData"
          >
            <app-detail-panel [dataSource]="dataSource.facility">
              <app-detail-item label="Facility Name" name="name" />
              <app-detail-item label="Contact Name" name="contactName" />
              <app-detail-item label="In Network" name="inNetwork" format="b" />
              <app-detail-item label="Phone" name="phone" format="p" />
              <app-detail-item label="Fax" name="fax" format="p" />
            </app-detail-panel>
          </app-card>
        </div>
        <div class="tw-w-full xl:tw-basis-1/3">
          <app-card
            label="Requesting Provider"
            emptyIcon="fa-light fa-user-doctor"
            emptyLabel="No Provider Information"
            emptyMessage="There is no information about Requesting Provider"
            [showEmptyState]="!providerHasData"
          >
            <app-detail-panel [dataSource]="dataSource.provider">
              <app-detail-item label="Provider" name="name" />
              <app-detail-item label="Specialty" name="specialty" />
              <app-detail-item label="In Network" name="inNetwork" format="b" />
              <app-detail-item label="Phone" name="phone" format="p" />
              <app-detail-item label="Fax" name="fax" format="p" />
            </app-detail-panel>
          </app-card>
        </div>
      </div>
      <!-- Request Card - ONLY for UR -->
      @if (isUR) {
        <div
          class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 md:tw-gap-8 tw-pb-8"
        >
          <div class="tw-w-full xl:tw-basis-2/3">
            <app-card
              label="Request"
              emptyIcon="fa-light fa-file-medical"
              emptyLabel="No Request Information"
              emptyMessage="There is no information about Request"
              [showEmptyState]="!requestHasData"
            >
              <app-detail-panel [dataSource]="dataSource.request">
                <app-detail-item
                  label="Specific request/determination"
                  name="specificRequest"
                />
                <app-detail-item
                  label="Clinical Summary"
                  name="clinicalSummary"
                />
                <app-detail-item label="Rationale" name="rationale" />
                <app-detail-item label="Guideline" name="guideline" />
                <app-detail-item label="Subset" name="guidelineSubset" />
                <app-detail-item
                  label="UR file recommendation"
                  name="urFileRecommendations"
                  labelCase="none"
                />
              </app-detail-panel>
            </app-card>
          </div>
          <div class="tw-w-full xl:tw-basis-1/3">
            <div class="tw-flex tw-flex-col tw-gap-4 md:tw-gap-8">
              <app-card
                label="Network Information"
                emptyIcon="fa-light fa-network-wired"
                emptyLabel="No Network Information"
                emptyMessage="There is no information about Network Information"
                [showEmptyState]="!networkInfoHasData"
              >
                <app-detail-panel [dataSource]="dataSource.networkInformation">
                  <app-detail-item
                    label="Referred Into"
                    name="referredIntoNetwork"
                    format="b"
                  />
                  <app-detail-item
                    label="Reason Not"
                    name="reasonNotReferred"
                  />
                </app-detail-panel>
              </app-card>
              <app-card
                label="Notifications"
                emptyIcon="fa-light fa-bell"
                emptyLabel="No Notifications"
                emptyMessage="There is no information about Notifications"
                [showEmptyState]="!notificationsHasData"
              >
                <app-detail-panel [dataSource]="dataSource.notifications">
                  <app-detail-item
                    label="Verbal Date"
                    name="employerNotifiedDate"
                    format="d"
                  />
                  <app-detail-item
                    label="Written Date Sent"
                    name="claimantNotifiedDate"
                    format="d"
                  />
                  <app-detail-item label="Spoke With" name="employerNotified" />
                  <app-detail-item
                    label="Date denial email to CE"
                    name="providerNotifiedDate"
                    format="d"
                    labelCase="none"
                  />
                  <app-detail-item
                    label="Voicemail"
                    name="voicemail"
                    format="b"
                  />
                  <app-detail-item label="Fax" name="fax" format="b" />
                  <app-detail-item label="Status">
                    <ng-template #value>
                      {{ dataSource.detail.status }}
                      {{ dataSource.detail.statusDate | format: 'dts' }}
                    </ng-template>
                  </app-detail-item>
                </app-detail-panel>
              </app-card>
            </div>
          </div>
        </div>
      }
      <!-- Diagnosis Card - ONLY for UR -->
      @if (isUR) {
        <div
          class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 md:tw-gap-8 tw-pb-4"
        >
          <div class="tw-basis-full">
            <app-card label="Diagnosis">
              <app-table
                [dataSource]="dataSource.diagnoses"
                [pageSize]="5"
                recordLabel="Diagnosis"
                recordLabelPlural="Diagnoses"
              >
                <app-column name="type" label="Type" width="10%" />
                <app-column name="code" label="Code" width="10%" />
                <app-column
                  name="description"
                  label="Short Description"
                  width="20%"
                />
                <app-column name="site" label="Site" width="15%" />
                <app-column name="location" label="Location" width="15%" />
                <app-column name="status" label="Status" width="10%" />
                <app-column
                  name="statusDate"
                  label="Status Date"
                  format="d"
                  width="10%"
                />
                <app-column
                  name="assignedDate"
                  label="Assigned Date"
                  format="d"
                  width="10%"
                />
                <app-column
                  name="longDescription"
                  label="Long Description"
                  [fullRow]="true"
                />
              </app-table>
            </app-card>
          </div>
        </div>
      }
      <!-- Procedures Card - Available for both UR and NonUR -->
      <div
        class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 md:tw-gap-8 tw-pb-4"
      >
        <div class="tw-basis-full">
          <app-card label="Procedures">
            <app-table
              [dataSource]="dataSource.procedures"
              [pageSize]="5"
              recordLabel="Procedure"
            >
              <app-column name="status" label="Status" width="10%" />
              <app-column name="code" label="Procedure Code" width="10%" />
              <app-column
                name="requestedQuantity"
                label="Requested Amount"
                format="c"
                width="10%"
              />
              <app-column
                name="approvedQuantity"
                label="Application Amount"
                format="c"
                width="10%"
              />
              <app-column
                name="totalFee"
                label="Total Fee"
                format="c"
                width="10%"
              />
              <app-column
                name="startDate"
                label="Start Date"
                format="d"
                width="15%"
              />
              <app-column
                name="endDate"
                label="End Date"
                format="d"
                width="15%"
              />
              <app-column name="category" label="Category" width="20%" />
              <app-column
                name="description"
                label="Description"
                [fullRow]="true"
              />
            </app-table>
          </app-card>
        </div>
      </div>
    }
  `,
  styles: [],
})
export class CaseTreatmentPlanComponent
  extends BaseComponent
  implements OnInit
{
  @Input() treatmentPlanId: number | null = null;
  @Input() treatmentPlanType: 'UR' | 'Non-UR' = 'UR';
  @Output() backToList = new EventEmitter<void>();

  dataSource: any = null;
  loading = true;
  error: any = null;
  apiResponse: TreatmentPlanDetailResponse | null = null;
  isUR = false;
  isNonUR = true;
  facilityHasData = false;
  providerHasData = false;
  requestHasData = false;
  networkInfoHasData = false;
  notificationsHasData = false;

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    if (!this.treatmentPlanId) {
      this.loading = false;
      return;
    }

    await this.loadTreatmentPlanDetail();
    this.setDisplayFlags();
  }

  @HandleLoadingState()
  private async loadTreatmentPlanDetail(): Promise<void> {
    if (!this.treatmentPlanId) {
      return;
    }

    const claimKey = getClaimKey();

    this.apiResponse = await this.api.caseManagement.getTreatmentPlanDetail(
      claimKey,
      this.treatmentPlanId,
      this.treatmentPlanType,
    );

    this.dataSource = this.transformResponseToViewModel(this.apiResponse);
  }

  private setDisplayFlags(): void {
    const { facility, provider, notifications, type } = this.apiResponse || {};

    this.isUR = type === 'UR' || this.treatmentPlanType?.toUpperCase() === 'UR';
    this.isNonUR = !this.isUR;

    this.facilityHasData = !!(
      facility &&
      (facility.name || facility.contactName || facility.phone || facility.fax)
    );

    this.providerHasData = !!(
      provider &&
      (provider.name || provider.specialty || provider.phone || provider.fax)
    );

    this.requestHasData = !!(
      this.apiResponse &&
      (this.apiResponse.specificRequest ||
        this.apiResponse.clinicalSummary ||
        this.apiResponse.rationale ||
        this.apiResponse.guideline ||
        this.apiResponse.guidelineSubset ||
        this.apiResponse.urFileRecommendations)
    );

    this.networkInfoHasData = !!(
      this.apiResponse &&
      (this.apiResponse.referredIntoNetwork ||
        this.apiResponse.reasonNotReferred)
    );

    this.notificationsHasData = !!(
      notifications &&
      (notifications.employerNotified ||
        notifications.employerNotifiedDate ||
        notifications.claimantNotifiedDate ||
        notifications.providerNotifiedDate ||
        notifications.voicemail != null ||
        notifications.fax != null)
    );
  }

  private transformResponseToViewModel(
    response: TreatmentPlanDetailResponse,
  ): any {
    return {
      detail: response,
      facility: response.facility,
      provider: response.provider,
      request: response,
      networkInformation: response,
      notifications: response.notifications,
      diagnoses: (response.diagnoses || []).map((d) => ({
        ...d,
        type: d.type || (d.isPrimary ? 'Primary' : 'Secondary'),
      })),
      procedures: response.procedures || [],
    };
  }

  onBackClick(): void {
    this.backToList.emit();
  }
}
